<template>
  <Modal
    ref="modal"
    size="2xl"
    @close="$emit('close')"
  >
    <template #icon>
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="w-5 h-5 text-blue-600">
    <path d="M9 12L11 14L15 9.99999M20 12C20 16.4611 14.54 19.6937 12.6414 20.683C12.4361 20.79 12.3334 20.8435 12.191 20.8712C12.08 20.8928 11.92 20.8928 11.809 20.8712C11.6666 20.8435 11.5639 20.79 11.3586 20.683C9.45996 19.6937 4 16.4611 4 12V8.21759C4 7.41808 4 7.01833 4.13076 6.6747C4.24627 6.37113 4.43398 6.10027 4.67766 5.88552C4.9535 5.64243 5.3278 5.50207 6.0764 5.22134L11.4382 3.21067C11.6461 3.13271 11.75 3.09373 11.857 3.07827C11.9518 3.06457 12.0482 3.06457 12.143 3.07827C12.25 3.09373 12.3539 3.13271 12.5618 3.21067L17.9236 5.22134C18.6722 5.50207 19.0465 5.64243 19.3223 5.88552C19.566 6.10027 19.7537 6.37113 19.8692 6.6747C20 7.01833 20 7.41808 20 8.21759V12Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
    </template>

    <template #header>
      {{ $t('ProjectSettings.CompanyTab.CompanyUsersTab.modals.rolesPermissions.title') }}
    </template>

    <template #body>
      <div class="space-y-4">
        <p class="text-sm text-gray-600 mb-6">
          {{ $t('ProjectSettings.CompanyTab.CompanyUsersTab.modals.rolesPermissions.description') }}
        </p>

        <!-- Roles and Permissions Table -->
        <div class="overflow-x-auto border border-gray-200 rounded-lg">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50 z-10">
                  {{ $t('ProjectSettings.CompanyTab.CompanyUsersTab.modals.rolesPermissions.table.role') }}
                </th>

                <th
                  v-for="permission in permissions"
                  :key="permission.key"
                  class="p-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[120px]"
                >
                  {{ permission.label }}
                </th>
              </tr>
            </thead>

            <tbody class="bg-white divide-y divide-gray-200">
              <tr
                v-for="role in roles"
                :key="role.name"
                class="hover:bg-gray-50"
              >
                <td class="p-4 whitespace-nowrap sticky left-0 bg-white z-10">
                  <div class="flex items-center">
                    <div class="flex-shrink-0">
                      <div class="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4 text-blue-600">
  <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" />
</svg>
                      </div>
                    </div>

                    <div class="ml-3">
                      <div class="text-sm font-medium text-gray-900">
                        {{ role.descriptor }}
                      </div>
                    </div>
                  </div>
                </td>

                <td
                  v-for="permission in permissions"
                  :key="`${role.name}-${permission.key}`"
                  class="px-3 py-4 whitespace-nowrap text-center"
                >
                  <div class="flex justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" v-if="hasPermission(role.name, permission.key)"
                     
                      class="w-5 h-5 text-green-600">
  <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5" />
</svg>

                    <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
 v-else
                     
                      class="w-5 h-5 text-gray-300">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M6 18L18 6M6 6l12 12"
    />
</svg>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="flex justify-end">
        <button
          type="button"
          class="btn--secondary-blue btn"
          @click="modal.close"
        >
          {{ $t('ProjectSettings.CompanyTab.CompanyUsersTab.modals.rolesPermissions.buttons.close') }}
        </button>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import Modal from '@/components/Modal.vue';
import { useRolesStore } from '@/store/roles';
import { computed, useTemplateRef } from 'vue';

const emits = defineEmits<{
  close: [];
}>();

const modal = useTemplateRef('modal');
const rolesStore = useRolesStore();

// Define roles in order of hierarchy
const roles = computed(() => {
  const roleOrder = [
    'company_admin',
    'project_admin',
    'contract_admin',
    'contract_manager',
    'site_manager',
    'engineer',
    'client_manager',
  ];

  return rolesStore.models.whereIn('name', roleOrder).get();
});

// Define permissions based on policy analysis
const permissions = [
  {
    key: 'manage_users',
    label: 'Manage Users',
    description: 'Can invite and manage users',
  },
  {
    key: 'create_projects',
    label: 'Create Projects',
    description: 'Can create new projects',
  },
  {
    key: 'update_projects',
    label: 'Update Projects',
    description: 'Can modify project settings and details',
  },
  {
    key: 'delete_projects',
    label: 'Delete Projects',
    description: 'Can delete projects',
  },
  {
    key: 'archive_projects',
    label: 'Archive Projects',
    description: 'Can archive projects',
  },
  {
    key: 'export_projects',
    label: 'Export Reports',
    description: 'Can export reports',
  },
  {
    key: 'manage_head_contracts',
    label: 'Head Contracts Settings',
    description: 'Can manage head contract settings',
  },
  {
    key: 'manage_subcontracts',
    label: 'Subcontract Settings',
    description: 'Can manage subcontract settings',
  },
  {
    key: 'manage_self_perform',
    label: 'Self Perform Settings',
    description: 'Can manage self perform settings',
  },
  {
    key: 'lock_boq',
    label: 'Lock BOQ',
    description: 'Can lock Bill of Quantities',
  },
  {
    key: 'create_trades',
    label: 'Modify BoQ',
    description: 'Can modify bill of quantities',
  },
  {
    key: 'modify_locked_boq',
    label: 'Modify Locked BoQ',
    description: 'Can modify BoQ after lock',
  },
  {
    key: 'modify_development_costs',
    label: 'Development Costs',
    description: 'Can modify development costs',
  },
  {
    key: 'view_claims',
    label: 'Claims',
    description: 'Can make claims',
  },
  {
    key: 'modify_variations',
    label: 'Variations',
    description: 'Can manage variations',
  },
  {
    key: 'modify_delays',
    label: 'Delays',
    description: 'Can manage delays',
  },
  {
    key: 'modify_forecast',
    label: 'Forecasts',
    description: 'Can manage forecasts',
  },
  {
    key: 'modify_baseline',
    label: 'Baseline',
    description: 'Can manage baseline',
  },
  {
    key: 'modify_packages',
    label: 'Packages',
    description: 'Can manage packages',
  },
  {
    key: 'subcontract_claims',
    label: 'Subcontract Claims',
    description: 'Can manage subcontract claims',
  },
  {
    key: 'manage_plants',
    label: 'Plants',
    description: 'Can manage plants',
  },
  {
    key: 'manage_labour',
    label: 'Labour',
    description: 'Can manage labour',
  },
  {
    key: 'modify_quantities',
    label: 'Quantities',
    description: 'Can modify quantities',
  },
  {
    key: 'modify_cost_at_completion',
    label: 'Cost at Completion',
    description: 'Can modify cost at completion',
  },
];

// TODO: review permissions
const permissionMatrix: Record<string, string[]> = {
  company_admin: [
    'manage_users',
    'create_projects',
    'update_projects',
    'delete_projects',
    'archive_projects',
    'export_projects',
    'manage_head_contracts',
    'manage_subcontracts',
    'manage_self_perform',
    'lock_boq',
    'create_trades',
    'modify_locked_boq',
    'modify_development_costs',
    'view_claims',
    'modify_variations',
    'modify_delays',
    'modify_forecast',
    'modify_baseline',
    'modify_packages',
    'subcontract_claims',
    'manage_plants',
    'manage_labour',
    'modify_quantities',
    'modify_cost_at_completion',
  ],

  project_admin: [
    'archive_projects',
    'update_projects',
    'export_projects',
    'manage_head_contracts',
    'manage_subcontracts',
    'lock_boq',
    'create_trades',
    'modify_locked_boq',
    'modify_development_costs',
    'view_claims',
    'modify_variations',
    'modify_delays',
    'modify_forecast',
    'modify_baseline',
    'modify_packages',
    'subcontract_claims',
    'manage_plants',
    'manage_labour',
    'modify_quantities',
    'modify_cost_at_completion',
  ],

  contract_admin: [
    'export_projects',
    'manage_head_contracts',
    'manage_subcontracts',
    'lock_boq',
    'create_trades',
    'modify_locked_boq',
    'modify_development_costs',
    'view_claims',
    'modify_variations',
    'modify_delays',
    'modify_forecast',
    'modify_baseline',
    'modify_packages',
    'subcontract_claims',
  ],

  contract_manager: [
    'manage_head_contracts',
    'manage_subcontracts',
    'lock_boq',
    'create_trades',
    'modify_development_costs',
    'view_claims',
    'modify_variations',
    'modify_delays',
    'modify_forecast',
    'modify_baseline',
    'modify_packages',
    'subcontract_claims',
  ],

  site_manager: [
    'manage_self_perform',
    'lock_boq',
    'create_trades',
    'modify_locked_boq',
    'modify_development_costs',
    'manage_plants',
    'manage_labour',
    'manage_labour',
    'modify_quantities',
    'modify_cost_at_completion',
  ],

  engineer: [
    'manage_self_perform',
    'manage_plants',
    'manage_labour',
    'manage_labour',
    'modify_quantities',
    'modify_cost_at_completion',
  ],

  client_manager: [
    'modify_development_costs',
    'view_claims',
  ],
};

const hasPermission = (roleName: string, permissionKey: string): boolean => {
  return permissionMatrix[roleName]?.includes(permissionKey) || false;
};
</script>

<style scoped></style>
